import { BinIcon, PenIcon, SaveIcon } from '@systemeio/ui/icons'
import { FormSelect, PrimaryButton, Tooltip, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { DigitalProductBase } from 'modules/digital-product/components/digital-product-base'
import { DigitalProductTabEnum } from 'modules/digital-product/enums/digital-product-tab-enum'
import { DigitalProductProps } from 'modules/digital-product/index'
import { PricePlansReadOnly } from 'modules/digital-product/price-plans/components/price-plans-read-only'
import { ResourcesReadOnly } from 'modules/digital-product/resources/components/resources-read-only'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
  DigitalProductId,
  DigitalProductInterface,
} from 'modules/digital-product/types/digital-product-interface'
import { getDigitalProductsData } from 'modules/digital-product/utils/get-digital-products-data'
import { AddItemIcon } from 'modules/funnels/funnel/components/offer-settings/add-item-icon'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { defaultDigitalProductErrors } from '../constants/default-values'

interface DigitalProductSelectProps
  extends Omit<
    DigitalProductProps,
    'paymentMethods' | 'mutateDigitalProduct' | 'currency' | 'product' | 'source'
  > {
  digitalProductList: DigitalProductInterface[]
  setTab: (tab: DigitalProductTabEnum) => void
  digitalProductId?: DigitalProductId
  onDelete: (id: DigitalProductId) => void
}

const DigitalProductSelect: FC<DigitalProductSelectProps> = ({
  digitalProductId,
  onSave,
  isBump,
  digitalProductList,
  setTab,
  onDelete,
}) => {
  const [id, setId] = useState<DigitalProductChangeInterface['id']>(digitalProductId ?? null)
  const { t } = useLocoTranslation()
  const [hasChanged, setHasChanged] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [errors, setErrors] = useState<DigitalProductErrorInterface>(defaultDigitalProductErrors)

  useEffect(() => {
    setId(digitalProductId ?? null)
  }, [digitalProductId])

  const selectedProduct = digitalProductList.find(product => product.id === id)

  const handleSave = async () => {
    setIsFetching(true)
    try {
      await onSave(selectedProduct?.id ?? null)
      setHasChanged(false)
      setIsFetching(false)
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors.fields

        if (errors) {
          setErrors(prev => ({ ...prev, ...errors }))

          if (errors.digitalProduct) {
            toast.error(errors.digitalProduct.join(', '))
          }
        }
      }
    } finally {
      setIsFetching(false)
    }
  }

  const hasResources = selectedProduct && selectedProduct.resources.length !== 0

  const hasPricePlans = selectedProduct && selectedProduct.pricePlans.length !== 0

  const showDetails = hasResources || hasPricePlans

  return (
    <>
      <DigitalProductBase
        additionalChild={
          <div className={'flex items-center gap-3'}>
            {!hasChanged && digitalProductId && (
              <Tooltip
                mode={'hover'}
                label={t('dashboard.funnel.configuration.offer.edit_digital_product')}
              >
                <button
                  onClick={() => setTab(DigitalProductTabEnum.edit)}
                  className={'group outline-hidden'}
                >
                  <PenIcon
                    className={
                      'group-hover:fill-brand-primary main-transition-colors h-[20px] w-[20px]'
                    }
                  />
                </button>
              </Tooltip>
            )}
            <AddItemIcon onClick={() => setTab(DigitalProductTabEnum.create)} />
          </div>
        }
      />
      <FormSelect
        inputRef={inputRef}
        data={getDigitalProductsData(
          digitalProductList,
          id => (
            <button
              className={'group px-2'}
              // necessary to prevent headless-ui from element selection
              onMouseDown={e => {
                e.stopPropagation()
              }}
              onClick={e => {
                e.stopPropagation()
                if (inputRef.current) {
                  inputRef.current.dispatchEvent(
                    new KeyboardEvent('keydown', {
                      key: 'Escape',
                      code: 'Escape',
                      bubbles: true,
                      cancelable: true,
                    }),
                  )
                }
                onDelete(id)
              }}
            >
              <BinIcon className="group-hover:fill-red h-4 w-4" />
            </button>
          ),
          isBump,
        )}
        className={'sm:min-w-0'}
        value={id || undefined}
        onChange={data => {
          setId(data)
          setHasChanged(data !== digitalProductId)
        }}
      />
      {hasChanged && (
        <PrimaryButton
          width="large"
          onClick={handleSave}
          isFetching={isFetching}
          type="submit"
          className="font-inter mr-auto text-sm font-bold"
        >
          <SaveIcon />
          {t('global.save')}
        </PrimaryButton>
      )}
      {showDetails && (
        <div className={'ml-5 flex flex-col gap-5'}>
          <ResourcesReadOnly resources={selectedProduct.resources} errors={errors.resources} />
          <hr className={'border-t-1 border-text-secondary/30 border-solid'} />
          <PricePlansReadOnly pricePlans={selectedProduct.pricePlans} />
        </div>
      )}
    </>
  )
}

export default DigitalProductSelect
